
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Actions, ActionStates } from '../lib/Actions';
import { container } from '../lib/container';
import { ContainerTypes } from '../lib/core/types/ContainerTypes';
import { isResultEntry, isNotEmptyString, isLatLngLiteral, isGeocoderEntry } from '../lib/core/guards';
import { ResultEntry } from '../lib/interfaces/search/ResultEntry';
import { Referrer, RouteTargetEntry } from '../lib/interfaces/route/RouteTargetEntry';
import { getOpenTimesFor, shortDays } from '../lib/core/OpeningHours';
import { OpeningHoursContainer, OpenTimes } from '../lib/interfaces/search/OpeningHours';
import { I18n } from '../lib/interfaces/I18n';
import deDE from '../lib/presets/resources/de-DE';
import { onlyDevice, allowDevices, gtDevice } from '../lib/core/responsive';
import { SearchStorage } from '../lib/core/storage/SearchStorage';
import { GeocoderEntry } from '../lib/interfaces/search/GeocoderEntry';
import { FreeTextItem } from '../lib/interfaces/search/additionalItems/FreeTextItem';
import { setCmd, execCmd } from '../lib/core/storage/CommandStorage';
import { DetailsStorage } from '../lib/core/storage/DetailsStorage';
import SimpleBar from './simplebar.vue';
import { decodeHTML, formatAddressLine } from '../lib/core/util';
import { getConfig } from '../lib/ConfigLoader';
import convertUnits from 'convert-units';
import { AppBehavior } from '../lib/core/AppBehavior';

const behavior = container.get<AppBehavior>('behavior');

import EkomiStars from './ekomi-stars.vue';
import { MutationTypes } from '../store/mutation-types';

const actions = container.get<Actions>(ContainerTypes.Action);
const storage = container.get<SearchStorage>(ContainerTypes.SearchStorage);
const detailsStorage = container.get<DetailsStorage>(ContainerTypes.DetailsStorage);

actions.on(ActionStates.CallDetails, (from, event) => {
	execCmd('callDetails', from, event);
});

actions.on(ActionStates.UpdateDetails, (from, event) => {
	execCmd('updateDetails', from, event);
});

actions.on(ActionStates.Idle, (from, event) => {
	execCmd('detailsIdle', from, event);
});

actions.on(ActionStates.CallRouting, (from, event) => {
	execCmd('detailsCallRouting', from, event);
});

actions.on(ActionStates.HomePage, () => execCmd('reset'));

@Component({ components: { SimpleBar, EkomiStars } })
export default class DetailsView extends Vue {
	@Prop() provider!: any;
	@Prop() activated!: boolean;
	@Prop() defaultEntry!: ResultEntry;
	entry!: ResultEntry;

	isActivated = false;
	isInitialized = false;
	previewMode = false;

	i18n: I18n = deDE;

	infoIcons: Array<{
		label: string;
		icon: string;
	}> = [];

	get config() {
		return getConfig();
	}

	distance() {
		return convertUnits(parseFloat(this.entry.address.distance || '0'))
			.from('m')
			.to(this.i18n.lengthMeasurement)
			.toFixed(2);
	}

	distanceUnit() {
		return convertUnits().describe(this.i18n.lengthMeasurement).abbr;
	}

	vcardLink() {
		const config = getConfig<(entry: ResultEntry) => string>();
		if ('string' === typeof config.detailsView.vcard.redirectHref) {
			return config.detailsView.vcard.redirectHref;
		}
		return config.detailsView.vcard.redirectHref(this.entry);
	}

	vcardDownloadName() {
		return `${this.entry.title}.vcf`;
	}

	$refs!: {
		detailsContainer: HTMLElement;
	};

	decodeHTML = decodeHTML;

	isActive() {
		return this.entry && (this.activated || this.isActivated);
	}

	formatTime(time: string): string {
		return time.replace(/[.:-]/, this.i18n.businessHours.delimiter);
	}

	vcardLinkTarget() {
		const config = getConfig();
		return config.detailsView.vcard.redirectTarget;
	}

	get canHaveAppointment(): boolean {
		const config = getConfig();
		return config.detailsView.vcard.enabled && this.$store.state.isAppView == false;
	}

	canHaveRouting() {
		const config = getConfig();
		return config.routing.enabled;
	}

	leavePreviewMode() {
		if (this.previewMode) {
			this.previewMode = false;
			this.$store.commit(MutationTypes.detailViewChangeMode, this.previewMode ? 'preview' : 'full');
		}
	}

	isNonPublic() {
		return this.entry.branchCodes.some(bc => 'CPxDEGxNPB' === bc);
	}

	isFreeTextArray(obj: any): obj is FreeTextItem[] {
		return obj && obj.length;
	}

	hasAdditionalOpeningHours() {
		if (isResultEntry(this.entry) && this.isFreeTextArray(this.entry.additions.freeText)) {
			const freeText = this.entry.additions.freeText;
			for (const ft of freeText) {
				if (
					ft.description.indexOf('<strong>Öffnungszeiten:</strong>') === 0 ||
					ft.description.indexOf('Nachtschließzeit') === 0
				) {
					return true;
				}
			}
		}
		return false;
	}

	hasBusinessHours() {
		const config = getConfig();
		return config.detailsView.businessHours.enabled && this.entry.businessHours.openAt.length;
	}

	hasTextBusinessHours() {
		const config = getConfig();
		return config.detailsView.businessHours.enabled && this.entry.businessHours.text.length > 0;
	}

	getTextOpeningHours() {
		if (isResultEntry(this.entry) && this.isFreeTextArray(this.entry.additions.freeText)) {
			const freeText = this.entry.additions.freeText;
			const openingHours = freeText.find(
				(ft: FreeTextItem) => ft.description.indexOf('<strong>Öffnungszeiten:</strong>') === 0
			);
			return openingHours ? openingHours.description : null;
		}
		return null;
	}

	getNightClosingTime() {
		if (isResultEntry(this.entry) && this.isFreeTextArray(this.entry.additions.freeText)) {
			const freeText = this.entry.additions.freeText;
			const nightClosingTime = freeText.find(
				(ft: FreeTextItem) => ft.description.indexOf('Nachtschließzeit') === 0
			);
			return nightClosingTime ? nightClosingTime.description : null;
		}
		return null;
	}

	hasFeatures() {
		// TODO: export features to independent modules
		return this.canHaveAppointment || this.canHaveRouting;
	}

	hasDefaultContact() {
		return (
			this.entry &&
			(this.entry.contact.tel.length || this.entry.contact.mobile.length || this.entry.contact.fax.length)
		);
	}

	hasAdditionalContact() {
		return this.entry && (this.entry.contact.email.length || this.entry.contact.url.length);
	}

	highlightToday(className: string, openTimes: OpenTimes, openingHours: OpeningHoursContainer) {
		const [today] = openingHours.today;
		return today && today.day === openTimes.day ? className + ' today' : className;
	}

	openDays(container: OpeningHoursContainer, index: number): OpenTimes[] {
		const days: OpenTimes[] = [];
		const entry = this.entry as ResultEntry;
		for (const day of shortDays) {
			const openTimes = getOpenTimesFor(day, container);
			openTimes.id = `business-hours-times-${entry.idDecoded}-${index}-${day}`;
			if (openTimes.times.length) {
				days.push(openTimes);
			}
		}
		return days;
	}

	onClose(event: Event) {
		event.preventDefault();
		if (this.isActivated || this.activated) {
			this.isActivated = false;
			const offset = {
				top: this.$refs.detailsContainer.offsetTop,
				right: 0,
				bottom: 0,
				left: this.$refs.detailsContainer.offsetLeft + this.$refs.detailsContainer.clientWidth
			};

			detailsStorage.offset = offset;
			actions.go(ActionStates.CallResults, {
				offset,
				pan: true,
				fitBounds: true
			});
			this.$store.commit(MutationTypes.detailViewSelectMarker);
		}
	}

	onOpenRouting(event: Event) {
		event.preventDefault();
		if (onlyDevice('mobile')) {
			if (isResultEntry(this.entry)) {
				this.redirectToApp(storage.geocoding[0] || storage.searchCenter, this.entry);
			}
		} else {
			actions.go(ActionStates.CallRouting, {
				referrer: Referrer.Details,
				...this.entry
			} as RouteTargetEntry);
			this.isActivated = false;
		}
	}

	createAppRouteLink(
		source: ym.provider.LatLngLiteral | string | GeocoderEntry | undefined,
		target?: ym.provider.LatLngLiteral | string | ResultEntry
	) {
		const isApple =
			navigator.platform.indexOf('iPhone') > -1 ||
			navigator.platform.indexOf('iPad') > -1 ||
			navigator.platform.indexOf('iPod') > -1;

		let domain: string = 'google';
		let protocol: string = 'https:';
		if (isApple) {
			domain = 'apple';
			protocol = 'maps:';
		}

		let sourceQuery = '';
		if (isGeocoderEntry(source)) {
			sourceQuery += formatAddressLine(source.address);
		} else if (isLatLngLiteral(source)) {
			sourceQuery += source.lat + ',' + source.lng;
		} else if (isNotEmptyString(source)) {
			sourceQuery += source;
		}

		let targetQuery = '';
		if (isResultEntry(target)) {
			targetQuery += formatAddressLine(target.address);
		} else if (isLatLngLiteral(target)) {
			targetQuery += target.lat + ',' + target.lng;
		} else if (isNotEmptyString(target)) {
			targetQuery += target;
		}

		sourceQuery = sourceQuery ? `&saddr=${sourceQuery}` : '';
		return `${protocol}//maps.${domain}.com/maps?daddr=${targetQuery}${sourceQuery}`;
	}

	redirectToApp(
		source: ym.provider.LatLngLiteral | string | GeocoderEntry | undefined,
		target: ym.provider.LatLngLiteral | string | ResultEntry
	) {
		if (target) {
			window.open(this.createAppRouteLink(source, target));
		}
	}

	openDetailInfo(from?: ActionStates, event?: ResultEntry & { isMarkerClick?: boolean }) {
		const config = getConfig<(params: ResultEntry) => string>();

		this.previewMode = (behavior.appView && event?.isMarkerClick) || false;
		this.$store.commit(MutationTypes.detailViewChangeMode, this.previewMode ? 'preview' : 'full');

		if (isResultEntry(detailsStorage.entry)) {
			this.isActivated = false;
			if (!this.defaultEntry) {
				this.entry = detailsStorage.entry;
			}
			if (this.entry === detailsStorage.entry) {
				this.infoIcons = [];
				for (const infoIcon of config.detailsView.infoIcons) {
					if (this.entry.information.includes((<any>infoIcon).label)) {
						this.infoIcons.push(infoIcon);
					}
				}
			}

			// enable rendering of detailinfo
			this.isActivated = true;

			// every map action must take place afterwards
			this.$nextTick(() => {
				if (this.$refs.detailsContainer) {
					if (config.resultsView.scrolling.autoScroll && allowDevices(config.resultsView.scrolling.sizes)) {
						if (gtDevice('mobile')) {
							this.$refs.detailsContainer.scrollIntoView({
								behavior: 'smooth',
								block: 'start'
							});
						} else {
							const app = document.querySelector('#app');
							if (app) {
								app.scrollIntoView({ behavior: 'smooth', block: 'start' });
							}
						}
					}
				}
				actions.go(ActionStates.UpdateMap, {
					type: 'updateDetails',
					pan: true,
					position: this.entry.position
				});
			});
		}
	}

	created() {
		const config = getConfig();
		if (!this.entry && this.defaultEntry) {
			this.entry = this.defaultEntry;
		}
		if (config.resultsView.scrolling.autoScroll && allowDevices(config.resultsView.scrolling.sizes)) {
			setCmd('detailsIdle', () => (this.isActivated = false));
			setCmd('detailsCallRouting', () => (this.isActivated = false));
			setCmd('updateDetails', (from, event) => this.openDetailInfo(from, event));
			this.$nextTick(() => actions.go(ActionStates.UpdateDetails, {}));
		}
	}

	mounted() {
		const config = getConfig();
		if (!config.resultsView.scrolling.autoScroll || !allowDevices(config.resultsView.scrolling.sizes)) {
			setCmd('detailsIdle', () => (this.isActivated = false));
			setCmd('detailsCallRouting', () => (this.isActivated = false));
			setCmd('updateDetails', (from, event) => this.openDetailInfo(from, event));
			setCmd('callDetails', (from, event) => actions.go(ActionStates.UpdateDetails, event));
		}

		setCmd('reset', () => (this.isActivated = false));
	}
}
